import { ALLOWED_DOCUMENT_EXTENSIONS } from "@etudescarre/constants";
import { Button, Modal } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";

// Écran 005 : consigner un compte-rendu de réunion client reçu hors
// ÉtudesCarré (N143), avec son fichier et ses pièces jointes.

type CompteRenduFormData = {
  number: string;
  dateReception: string;
  dateReunion: string;
  object: string;
};

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";
const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";
const fileInputClass =
  "w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400";
const acceptedExtensions = ALLOWED_DOCUMENT_EXTENSIONS.map(
  (ext) => `.${ext}`,
).join(",");

export type CompteRenduFormProps = {
  onCreated: () => void;
};

export function CompteRenduForm({ onCreated }: CompteRenduFormProps) {
  const [open, setOpen] = useState(false);

  return (
    // Unmounted when closed so each opening starts from an empty form.
    <Modal.Root
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      lazyMount
      unmountOnExit
    >
      <Button type="button" onClick={() => setOpen(true)}>
        <PlusIcon data-slot="icon" />
        Enregistrer un CR réunion
      </Button>
      <Modal.Portal>
        <Modal.Backdrop />
        <Modal.Positioner>
          <Modal.Content size="lg">
            <Modal.Header>
              <Modal.Title>Enregistrer un CR réunion</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <CompteRenduFields
                onCancel={() => setOpen(false)}
                onCreated={() => {
                  setOpen(false);
                  onCreated();
                }}
              />
            </Modal.Body>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}

function CompteRenduFields({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const { yard } = useYardContext();
  const [crFile, setCrFile] = useState<File | null>(null);
  const [pjFiles, setPjFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const form = useForm<CompteRenduFormData>({
    defaultValues: {
      number: "",
      dateReception: "",
      dateReunion: "",
      object: "",
    },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    const metadata = JSON.stringify({
      number: data.number.trim(),
      object: data.object.trim(),
      dateReception: new Date(data.dateReception).toISOString(),
      ...(data.dateReunion
        ? { dateReunion: new Date(data.dateReunion).toISOString() }
        : {}),
    });

    setSaving(true);
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/comptes-rendus",
        {
          params: { path: { yardId: String(yard.id) } },
          body: { metadata },
          bodySerializer: () => {
            const fd = new FormData();
            fd.append("metadata", metadata);
            if (isNotNil(crFile)) fd.append("file", crFile);
            for (const pj of pjFiles) fd.append("pj", pj);
            return fd;
          },
        },
      );
      if (isNotNil(error)) {
        toaster.error({
          title: "Erreur",
          description:
            error.error ?? "Échec de l'enregistrement du compte-rendu",
        });
        return;
      }
      toaster.success({
        title: "Succès",
        description: "Compte-rendu enregistré",
      });
      onCreated();
    } catch (err) {
      console.error("Failed to record compte-rendu", err);
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setSaving(false);
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label htmlFor="crNumber" className={labelClass}>
          N° du compte-rendu <span className="text-red-500">*</span>
        </label>
        <input
          id="crNumber"
          type="text"
          {...form.register("number")}
          required
          maxLength={255}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="crDateReception" className={labelClass}>
          Date de réception <span className="text-red-500">*</span>
        </label>
        <input
          id="crDateReception"
          type="date"
          {...form.register("dateReception")}
          required
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="crDateReunion" className={labelClass}>
          Date de la réunion
        </label>
        <input
          id="crDateReunion"
          type="date"
          {...form.register("dateReunion")}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="crObject" className={labelClass}>
          Objet de la réunion <span className="text-red-500">*</span>
        </label>
        <input
          id="crObject"
          type="text"
          {...form.register("object")}
          required
          maxLength={512}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="crFile" className={labelClass}>
          Fichier du compte-rendu
        </label>
        <input
          id="crFile"
          type="file"
          accept={acceptedExtensions}
          onChange={(e) => setCrFile(e.target.files?.[0] ?? null)}
          className={fileInputClass}
        />
      </div>

      <div>
        <label htmlFor="crPj" className={labelClass}>
          Pièces jointes
        </label>
        <input
          id="crPj"
          type="file"
          multiple
          accept={acceptedExtensions}
          onChange={(e) => setPjFiles(Array.from(e.target.files ?? []))}
          className={fileInputClass}
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" outline onClick={onCancel} disabled={saving}>
          Annuler
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? "En cours..." : "Enregistrer le compte-rendu"}
        </Button>
      </div>
    </form>
  );
}
